import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApolloQueryResult } from '@apollo/client/core';
import { Apollo, gql, MutationResult } from 'apollo-angular';
import { PagedRequest } from '../../models/api/shared/paged/paged-request';
import { PagedResponse } from '../../models/api/shared/paged/paged-response.interface';
import { map } from 'rxjs/operators';
import { VehicleModel, VehicleModelArray } from '../../models/entities/vehicle-model';
import { VehicleModelRequest } from '../../models/api/services/vehicle-model-requests.interface';
import { TypeSerializerUtils } from '../../utils/type-serializer.util';

@Injectable({
  providedIn: 'root'
})
export class VehicleModelsService {
  constructor(
    private readonly apollo: Apollo
  ) {}

  addVehicleModel(data: VehicleModelRequest): Observable<MutationResult<VehicleModel>> {
    const query = gql`
      mutation addVehicleModel($data: VehicleModelRequest) {
        params {
          vehicleModels {
            add (data: $data) {
              id
              active
              name
              category
              brand {
                id
                name
                logo
              }
            }
          }
        }
      }
    `;

    return this.apollo.mutate<{ params: { vehicleModels: { add: VehicleModel } } }>({
      mutation: query,
      variables: {
        data: {
          name: data.name,
          active: data.active,
          brand: {
            id: data.brand.id,
            name: data.brand.name
          },
          category: data.category,
        }
      }
    }).pipe(map(result => ({

      data: TypeSerializerUtils.deserialize(result.data?.params?.vehicleModels?.add, VehicleModel),
      errors: result.errors,
      extensions: result.extensions,
      loading: result.loading
    })));
  }

  updateVehicleModel(data: VehicleModelRequest): Observable<MutationResult<VehicleModel>> {
    const query = gql`
      mutation updateVehicleModel($id: Guid!, $data: VehicleModelRequest) {
        params {
          vehicleModels {
            update (id: $id, data: $data) {
              id
              active
              name
              category
              brand {
                id
                name
                logo
              }
            }
          }
        }
      }
    `;

    return this.apollo.mutate<{ params: { vehicleModels: { update: VehicleModel } } }>({
      mutation: query,
      variables: {
        id: data.id,
        data: {
          name: data.name,
          active: data.active,
          brand: {
            id: data.brand.id,
            name: data.brand.name
          },
          category: data.category,
        }
      }
    }).pipe(map(result => ({

      data: TypeSerializerUtils.deserialize(result.data?.params?.vehicleModels?.update, VehicleModel),
      errors: result.errors,
      extensions: result.extensions,
      loading: result.loading
    })));
  }

  vehicleModel(id: string): Observable<ApolloQueryResult<VehicleModel>> {
    const query = gql`
      query vehicleModel($id: Guid!) {
        params {
          vehicleModel(id: $id){
            id
            active
            name
            category
            brand {
              id
              name
              logo
            }
          }
        }
      }
    `;

    return this.apollo.query<{ params: { vehicleModel: VehicleModel } }>({
      query,
      variables: { id }
    }).pipe(map(result => ({
        data: TypeSerializerUtils.deserialize(result.data?.params?.vehicleModel, VehicleModel),
        error: result.error,
        errors: result.errors,
        partial: result.partial,
        loading: result.loading,
        networkStatus: result.networkStatus
      })));
  }

  allVehicleModels(parameters: PagedRequest): Observable<ApolloQueryResult<PagedResponse<VehicleModel>>> {
    const query = gql`
      query vehicleModels($offset: Int, $limit: Int, $sortProperty: String, $sortType: ESortType, $search: String) {
        params {
          vehicleModels(offset: $offset, limit: $limit, sortProperty: $sortProperty, sortType: $sortType, search: $search) {
            data {
              id
              active
              name
              category
              brand {
                id
                name
                logo
              }
            }
            metadata {
              currentPage
              currentResult
              totalPages
              totalResults
              hasMore
            }
          }
        }
      }
    `;
    return this.apollo.query<{ params: { vehicleModels: PagedResponse<VehicleModel> } }>({
      query,
      variables: {
        offset: parameters.offset,
        limit: parameters.limit,
        sortProperty: parameters.sortProperty,
        sortType: parameters.sortType,
        search: parameters.search
      }
    }).pipe(map(result => {
      const raw = result.data?.params?.vehicleModels;
      const response: PagedResponse<VehicleModel> = {
        data: TypeSerializerUtils.deserializeArr(raw.data, VehicleModelArray),
        metadata: raw.metadata
      };
      return {
        data: response,
        error: result.error,
        errors: result.errors,
        partial: result.partial,
        loading: result.loading,
        networkStatus: result.networkStatus
      };
    }));
  }

  deleteVehicleModel(...ids: string[]): Observable<MutationResult<void>> {
    const query = `
      mutation deleteModel(${ids.map((_, index) => '$id' + index + ': Guid!').join(', ')}) {
        params {
          vehicleModels {
            ${ ids.map((_, index) => 'id' + index + ': delete (id: $id' + index + ')').join('\n') }
          }
        }
      }
    `;

    const variables = ids.reduce((response, item, index) => {
      response['id' + index] = item;
      return response;
    }, {});

    return this.apollo.mutate<void>({
      mutation: gql(query),
      variables
    });
  }
}
